import {CHAT_CONFIG} from "./ChatConfiguration";
import type AuthStore from "../../../authentication/AuthStore";
import {B} from "../../../build";
import testChatData from './../testing/testChatData.json';
import {environment} from "../../../env.ts";

export class ChatApiStore {
    constructor(private authStore: AuthStore) {
    }

    async createSession(bagId: number): Promise<string> {
        if (environment.isTestChat) return 'TEST_SESSION_ID';
        try {
            const frontendUrl = window.location.href;
            const response = await fetch(`${CHAT_CONFIG.API_BASE_URL}/session`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.authStore.token}`,
                    'Content-Type': 'application/json',
                    'X-Frontend-Url': frontendUrl,
                },
                body: JSON.stringify({
                    databag_id: bagId
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to create session: ${response.statusText}`);
            }

            const data = await response.json();
            return data.session_id || data.id;
        } catch (error) {
            console.error('Error creating session:', error);
            throw error;
        }
    }

    createWebSocket(sessionId: string): WebSocket {
        if (environment.isTestChat) {
            return {} as any;
        }
        const token = B.APP_IS_DEV ? '22' : this.authStore.token;
        const wsUrl = `${CHAT_CONFIG.WS_BASE_URL}/${token}`;
        return new WebSocket(wsUrl);
    }

    async getDownloadUrl(filename: string): Promise<string> {
        try {
            const response = await fetch(`${CHAT_CONFIG.API_BASE_URL}/download/token/${filename}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${this.authStore.token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to find the file: ${response.statusText}`);
            }

            const data = await response.json();
            return data.download_url
        } catch (error) {
            console.error('Error creating session:', error);
            throw error;
        }
    }

    async checkHealth(): Promise<void> {
        try {
            await fetch(`${CHAT_CONFIG.API_BASE_URL}/health`, {
                method: 'GET',
            });
        } catch (error) {
            console.error('Health check failed:', error);
        }
    }

    async getSessionEvents(sessionId: string): Promise<any[]> {
        if (environment.isTestChat) {
            return testChatData as any[];
        }
        try {
            const response = await fetch(`${CHAT_CONFIG.API_BASE_URL}/session/events/${sessionId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${this.authStore.token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch session events: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching session events:', error);
            throw error;
        }
    }

    async listSessions(bagId: number): Promise<any[]> {
        if (environment.isTestChat) {
            return [];
        }
        try {
            const response = await fetch(`${CHAT_CONFIG.API_BASE_URL}/session/list?databag_id=${bagId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${this.authStore.token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch session list: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching session list:', error);
            throw error;
        }
    }

    async saveFeedback(
        sessionId: string,
        eventId: string,
        isHelpful: boolean,
        category: string | null = null,
        description: string | null = null
    ): Promise<void> {
        if (environment.isTestChat) {
            console.log('Test mode: Feedback would be saved', {sessionId, eventId, isHelpful, category, description});
            return;
        }
        try {
            const response = await fetch(`${CHAT_CONFIG.API_BASE_URL}/session/feedback`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.authStore.token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    event_id: eventId,
                    is_helpful: isHelpful,
                    category,
                    description,
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to save feedback: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error saving feedback:', error);
            throw error;
        }
    }
}

